/**
 * The drawings behind the icon NAMES of `navigation.ts` (docs/13-tasks/01-ui-chrome.md#18 §3).
 *
 * One shape language for all of them, so the rail reads as one set and not as fifteen clip-arts:
 * a 24×24 box, stroked in `currentColor` at 1.7 px, round caps and joins, nothing filled. Because
 * the stroke is `currentColor`, an icon NEVER declares a colour — it inherits the nav item's,
 * which is how the open item's icon turns to the accent without a second palette (rule S6).
 *
 * Each icon is stored as its list of path `d` strings rather than as its own component: fifteen
 * near-identical components is fifteen places to get the box, the stroke or the cap wrong.
 */
import type { IconName } from '../navigation.js';

/** The `d` of every path an icon is made of, in a 24×24 box. */
const ICON_PATHS: Readonly<Record<IconName, readonly string[]>> = {
  /* Đồng hồ đo — trang Tổng quan. */
  gauge: ['M4 18a8 8 0 1 1 16 0', 'M4 18h16', 'M12 18l4.2-4.6'],
  calendar: ['M4 7.5h16v12.5H4z', 'M4 11.5h16', 'M8 3.5v4', 'M16 3.5v4'],
  compass: ['M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z', 'M15.6 8.4l-2.1 5.1-5.1 2.1 2.1-5.1z'],
  /* Con tàu nhìn ngang: thân, cột, xà. */
  ship: ['M4 13.5h16l-2.4 6.5H6.4z', 'M12 13.5V5', 'M7.5 5h9', 'M12 3v2'],
  pie: ['M12 3a9 9 0 1 0 9 9h-9z', 'M14.5 3.6A9 9 0 0 1 20.4 9.5h-5.9z'],
  bars: ['M5 20V11.5', 'M12 20V4.5', 'M19 20v-6'],
  trend: ['M4 4.5v15h16', 'M7.5 15l3.5-4.5 3 2.5 4.5-6'],
  download: ['M12 4v10.5', 'M8 11l4 4 4-4', 'M4.5 19.5h15'],
  /* Mỏ neo — trang Cảng & bến. */
  anchor: [
    'M12 8.5V20',
    'M14 6.5a2 2 0 1 1-4 0 2 2 0 0 1 4 0z',
    'M5 12.5a7 7 0 0 0 14 0',
    'M8.5 11h7',
  ],
  layers: ['M12 3.5l8 4-8 4-8-4z', 'M4 12l8 4 8-4', 'M4 16.5l8 4 8-4'],
  box: ['M12 3.5l8 4v9l-8 4-8-4v-9z', 'M4 7.5l8 4 8-4', 'M12 11.5v9.5'],
  users: [
    'M11.5 10.5a3.4 3.4 0 1 1-6.8 0 3.4 3.4 0 0 1 6.8 0z',
    'M2.5 20c0-3.3 2.5-5.8 5.6-5.8S13.7 16.7 13.7 20',
    'M15.5 4.6a3.4 3.4 0 0 1 0 6.6',
    'M16.6 14.4c2.7.5 4.9 2.8 4.9 5.6',
  ],
  shield: [
    'M12 3.2l7 2.8v5.2c0 4.4-2.9 8.2-7 9.6-4.1-1.4-7-5.2-7-9.6V6z',
    'M9.2 11.8l2.1 2.1 3.9-4',
  ],
  /* Một người kèm cần gạt — trang Người dùng (quản trị tài khoản). */
  account: [
    'M14 8.5a3.6 3.6 0 1 1-7.2 0 3.6 3.6 0 0 1 7.2 0z',
    'M3 20.5c0-3.7 3-6.6 6.6-6.6',
    'M17.5 14.5v6',
    'M14.5 17.5h6',
  ],
  key: [
    'M20.5 7.5a3.7 3.7 0 1 1-7.4 0 3.7 3.7 0 0 1 7.4 0z',
    'M14.2 10.3L3.5 21v-2.6l1-1h2.2v-2.2h2.2v-2.2h2.3z',
  ],
  search: ['M17.5 10.8a6.7 6.7 0 1 1-13.4 0 6.7 6.7 0 0 1 13.4 0z', 'M15.8 15.8L21 21'],
  folder: ['M3.5 6.5h5.6l2 2.6h9.4V19a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 19z'],
  sliders: ['M4 8h9', 'M17 8h3', 'M4 16h5', 'M13 16h7', 'M15 5.5v5', 'M11 13.5v5'],
};

/**
 * The icon of a nav item or a group heading. `aria-hidden` always: the label is right next to it,
 * and when the rail is collapsed the label rides on the item's own `title`/`aria-label`.
 */
export function NavIcon({ name, size = 18 }: { name: IconName; size?: number }) {
  return (
    <svg
      className="ico"
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICON_PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/** Every name this file can draw — what the guard in `navigation.test.ts` compares against. */
export const DRAWN_ICONS: readonly IconName[] = Object.keys(ICON_PATHS) as IconName[];

/** ‹ / › for the collapse button. One path, mirrored by the direction. */
export function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={direction === 'left' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
    </svg>
  );
}
